/**
 * components/RightbarHost.tsx —— **右栏停靠面宿主**（形态／宽度／拖拽／折叠）。
 *
 * 真源：`102:frontend_src/src/components/RightbarHost.vue`（155 行）——**抄形态/尺寸/交互，译 Vue→React+TS**。
 * 依据：《UI 全局布局壳规范》v1.2 §5（右栏停靠面）／§2 座位 `rightbar`；施工方案 §4 U-B2。
 *
 * 契约（逐条承源件）：
 *  · slot→props：`tabStrip`／`tabBody`（源件 `<slot name="tab-strip"|"tab-body">`）；
 *  · 形态 `push`（占列，默认）／`fullscreen`（覆盖层）；**宽度手柄仅普通展开态**（`!collapsed && push`）；
 *  · **指针拖拽**（源件 0919 补，base−dx：手柄左移⇒右栏变宽）；
 *  · **键盘 ±16／Home-End**：Home 传下限 0、End 传 `Number.MAX_SAFE_INTEGER`，**极值不在本件算**（宿主 clamp）；
 *  · **折叠零代价**（§5：无轨条、无宽度）——collapsed ⇒ body/handle 均不渲染；
 *  · 宽度/折叠/形态**只经回调上报宿主**（本件不持业务态、零取数）。
 *
 * 纪律：色/间距/字号一律 `var(--xg-*)`（真名 token；见 styles/tokens/xg-tokens.css），**禁硬编码色值**；
 *      **禁 hover-only**（hover 反馈包进 `@media (hover:hover)`，focus-visible 提供非 hover 等价）。
 */
import React, { useRef, useState } from "react";

/** 停靠形态（承壳规范 §5；与 shell 侧 `RightbarForm` 同值域，本件独立定义免耦合 shell/**） */
export type RightbarPresentation = "push" | "fullscreen";

export interface RightbarHostProps {
  /** 停靠面状态（只读；宿主注入，本件不解释语义） */
  surface?: Record<string, unknown> | null;
  /** 右栏实际宽度（px，已解算）——拖拽/键盘基准 */
  width?: number;
  /** 是否可显示（普通形态下能否保住 300px 面板 + 400px 中栏；只读，宿主裁断） */
  canShow?: boolean;
  /** 形态：`push` | `fullscreen` */
  presentation?: RightbarPresentation;
  /** 是否折叠（折叠 ⇒ 零代价：不渲染 body/handle） */
  collapsed?: boolean;
  /** tab 条（承源件 slot `tab-strip`） */
  tabStrip?: React.ReactNode;
  /** tab 正文（承源件 slot `tab-body`） */
  tabBody?: React.ReactNode;
  /** 折叠/展开（承源件 emit `toggle-collapse`） */
  onToggleCollapse?: () => void;
  /** 正文只读（声明 `rightbar.readonly`；右栏＝详情面，不接上行命令） */
  readonly?: boolean;
  /** 切换形态（承源件 emit `toggle-presentation`） */
  onTogglePresentation?: () => void;
  /** 拖拽/键盘调整宽度，上报**新宽度值**（承源件 emit `resize`；宿主 clamp） */
  onResize?: (nextWidth: number) => void;
}

/** 键盘步长（源件 KEY_RESIZE_STEP=16：←/→ ±16px） */
const KEY_RESIZE_STEP = 16;

function classNames(...parts: Array<string | false>): string {
  return parts.filter(Boolean).join(" ");
}

/** 停靠面宿主 */
export function RightbarHost(props: RightbarHostProps): React.ReactElement {
  const { width = 0, presentation = "push", collapsed = true } = props; // 缺省＝折叠（源件 default:true 保守语义）
  const [dragging, setDragging] = useState(false);
  const dragBase = useRef(0);
  const dragStartX = useRef(0);

  const onHandlePointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    dragBase.current = width;
    dragStartX.current = e.clientX;
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  };

  const onHandlePointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!dragging) return;
    props.onResize?.(dragBase.current - (e.clientX - dragStartX.current)); // base − dx
  };

  const onHandlePointerUp = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!dragging) return;
    setDragging(false);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const onHandleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    switch (e.key) {
      case "ArrowLeft": // 分隔条左移 ⇒ 右栏变宽
        e.preventDefault(); props.onResize?.(width + KEY_RESIZE_STEP); break;
      case "ArrowRight": // 分隔条右移 ⇒ 右栏变窄
        e.preventDefault(); props.onResize?.(width - KEY_RESIZE_STEP); break;
      case "Home": // 下限极值：宿主 clamp ⇒ RIGHTBAR_MIN
        e.preventDefault(); props.onResize?.(0); break;
      case "End": // 上限极值：宿主 clamp ⇒ 视口 × 0.7
        e.preventDefault(); props.onResize?.(Number.MAX_SAFE_INTEGER); break;
      default: break;
    }
  };

  const showHandle = !collapsed && presentation === "push";
  const classes = classNames(
    "xg-rightbar-host",
    collapsed && "xg-rightbar-host--collapsed",
    presentation === "fullscreen" && "xg-rightbar-host--fullscreen",
    dragging && "xg-rightbar-host--dragging",
  );

  return (
    <div className={classes} data-testid="rightbar-host" data-collapsed={collapsed} data-form={presentation}>
      {showHandle ? (
        <div
          className="xg-rightbar-host__handle"
          data-testid="rightbar-handle"
          role="separator"
          aria-label="调整停靠面宽度"
          aria-orientation="vertical"
          tabIndex={0}
          onPointerDown={onHandlePointerDown}
          onPointerMove={onHandlePointerMove}
          onPointerUp={onHandlePointerUp}
          onPointerCancel={onHandlePointerUp}
          onDoubleClick={() => props.onToggleCollapse?.()}
          onKeyDown={onHandleKeyDown}
        />
      ) : null}

      {!collapsed ? (
        <div
          className="xg-rightbar-host__body"
          data-testid="rightbar-body"
          data-readonly={props.readonly === false ? "false" : "true"}
        >
          {props.tabStrip}
          {props.tabBody}
        </div>
      ) : null}

      <style>{STYLE}</style>
    </div>
  );
}

/** 自包含样式（源件 `<style scoped>` 译入；值一律走真名 token；类名 `xg-rightbar-host` 前缀隔离） */
import { rightbarHostStyle as STYLE } from "../styles/inlineStyles";

export default RightbarHost;
